import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #pageScrollY = 0;

  /** @type {Map<string, string>} */
  #recommendationsCache = new Map();

  /** @type {WeakMap<HTMLElement, any>} */
  #recommendationSwipers = new WeakMap();

  /** @type {number[]} */
  #recommendationRefreshTimeouts = [];

  /** @type {MutationObserver | null} */
  #recommendationsObserver = null;

  /** @type {boolean} */
  #isUpdatingRecommendations = false;

  /** @type {number} */
  #sliderLayoutRetries = 0;

  /** @type {number} */
  #swiperLoadRetries = 0;

  /** @type {number | null} */
  #scrollRestoreTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleRecommendationNav);
    this.addEventListener('click', this.#handleRecommendationAdd);
    this.#observeRecommendationContainer();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleRecommendationNav);
    this.removeEventListener('click', this.#handleRecommendationAdd);
    this.#historyAbortController?.abort();
    this.#clearRecommendationRefreshTimeouts();
    this.#recommendationsObserver?.disconnect();
    if (this.#scrollRestoreTimeout) window.clearTimeout(this.#scrollRestoreTimeout);
    document.documentElement.removeAttribute('dialog-scroll-restoring');
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#suppressHeaderScrollDuringRestore();
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      this.#removeCartDrawerHistoryEntry();
    }
  };

  /**
   * Closing the drawer programmatically restores the page scroll (fixed-body release, history
   * back, iOS settle). Those jumps would make the sticky header slide out and back in. Flag the
   * document for a short window so the header keeps its scroll reference in sync without animating.
   */
  #suppressHeaderScrollDuringRestore() {
    document.documentElement.setAttribute('dialog-scroll-restoring', '');
    if (this.#scrollRestoreTimeout) window.clearTimeout(this.#scrollRestoreTimeout);
    this.#scrollRestoreTimeout = window.setTimeout(() => {
      document.documentElement.removeAttribute('dialog-scroll-restoring');
      this.#scrollRestoreTimeout = null;
    }, 700);
  }

  #removeCartDrawerHistoryEntry() {
    const scrollY = this.#pageScrollY || window.scrollY;
    const previousScrollRestoration = history.scrollRestoration;
    let didRestoreScroll = false;

    const restoreScroll = () => {
      if (didRestoreScroll) return;

      didRestoreScroll = true;
      this.#restorePageScroll(scrollY, () => {
        history.scrollRestoration = previousScrollRestoration;
      });
    };

    history.scrollRestoration = 'manual';
    window.addEventListener('popstate', restoreScroll, { once: true });
    history.back();

    window.setTimeout(() => {
      if (history.state?.cartDrawerOpen) restoreScroll();
    }, 1000);
  }

  /**
   * iOS Safari can adjust scroll after dialog focus restoration and history cleanup.
   * Restoring over the next few frames keeps the page anchored where the drawer opened.
   * @param {number} scrollY
   * @param {() => void} [callback]
   */
  #restorePageScroll(scrollY, callback) {
    const restore = () => {
      if (Math.abs(window.scrollY - scrollY) > 1) {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }
    };

    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });

    [50, 150, 350].forEach((delay, index, delays) => {
      window.setTimeout(() => {
        restore();
        if (index === delays.length - 1) callback?.();
      }, delay);
    });
  }

  #handleDialogOpen = () => {
    this.#queueRecommendationsRefresh();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.#suppressHeaderScrollDuringRestore();
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    if (this.refs.dialog?.open) {
      this.#queueRecommendationsRefresh();
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Scrolls the empty cart recommendations rail.
   * @param {MouseEvent} event
   */
  #handleRecommendationNav = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-recommendations-direction]') : null;
    if (!button) return;

    event.preventDefault();
  };

  #handleRecommendationAdd = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-recommendation-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!addResponse.ok) {
        throw new Error(`Failed to add variant ${variantId} to cart`);
      }

      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      this.#recommendationsCache.clear();

      document.dispatchEvent(
        new CartAddEvent(cart, 'cart-drawer-recommendations', {
          source: 'cart-drawer-recommendations',
          itemCount: cart.item_count,
          variantId,
        })
      );
    } catch (error) {
      console.error('Cart drawer recommendation add error:', error);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  };

  showDialog() {
    this.#pageScrollY = window.scrollY;
    super.showDialog();
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  /**
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();

    if (event?.target instanceof HTMLElement) {
      event.target.blur();
    }

    this.#suppressHeaderScrollDuringRestore();

    // Mobile: pop the pushed history entry *now*, while the body is still scroll-locked from being
    // open. Doing `history.back()` before `closeDialog()` releases the lock keeps the iOS scroll
    // reset invisible, so the page (and the sticky header) no longer jump to the top and animate
    // back down on close. `closeDialog()` then restores the real scroll position after the close
    // animation, and since the `cartDrawerOpen` state is already gone, `#handleHistoryClose` won't
    // pop a second time. (`#handlePopState` is detached by the abort so it can't re-fire.)
    if (isMobileBreakpoint() && history.state?.cartDrawerOpen) {
      this.#historyAbortController?.abort();
      history.back();
    }

    // `closeDialog()` restores the page scroll on its own (un-fixes the body and scrolls back to
    // the stored position once the close animation ends), so we must not also call
    // `#restorePageScroll` here — competing `window.scrollTo` calls were what made the header jump.
    this.closeDialog();
  }

  #observeRecommendationContainer() {
    this.#recommendationsObserver?.disconnect();

    this.#recommendationsObserver = new MutationObserver((mutations) => {
      if (!this.refs.dialog?.open || this.#isUpdatingRecommendations) return;

      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;
        if (!mutation.addedNodes.length && !mutation.removedNodes.length) continue;

        // Section morphs wipe the JS-rendered slides (the server renders the track empty),
        // so any child change on the track itself must trigger a refill.
        if (mutation.target instanceof Element && mutation.target.closest('[data-cart-recommendations-track]')) {
          this.#queueRecommendationsRefresh();
          return;
        }

        // A section morph can also re-render the pagination empty — stripping the Swiper fraction
        // spans WITHOUT touching the track — which makes the count vanish a moment after it first
        // renders. Detect that removal and rebuild. We react only to REMOVED nodes (and our own
        // re-render is suppressed via `#isUpdatingRecommendations`), so Swiper adding the spans
        // back can't feed another refresh and loop.
        if (
          mutation.target instanceof Element &&
          mutation.target.closest('[data-cart-recommendations]') &&
          [...mutation.removedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches(
                '.swiper-pagination-current, .swiper-pagination-total, .swiper-pagination, .swiper-scrollbar-drag, .swiper-scrollbar'
              ) ||
                !!node.querySelector?.('.swiper-pagination-current, .swiper-pagination-total, .swiper-scrollbar-drag'))
          )
        ) {
          // Re-render the fraction synchronously — still inside this observer microtask, before the
          // browser paints — so the morph-wiped count is restored in the same frame and never
          // flashes empty. Suppress our own mutations so the re-render can't loop back here.
          this.#isUpdatingRecommendations = true;
          this.#restoreRecommendationControls();
          window.requestAnimationFrame(() => {
            this.#isUpdatingRecommendations = false;
          });
          return;
        }

        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
        const shouldRefresh = changedNodes.some((node) => {
          if (!(node instanceof HTMLElement)) return false;

          return (
            node.matches?.('[data-cart-recommendations], cart-items-component, .cart-drawer__inner') ||
            node.querySelector?.('[data-cart-recommendations], cart-items-component, .cart-drawer__inner')
          );
        });

        if (shouldRefresh) {
          this.#queueRecommendationsRefresh();
          return;
        }
      }
    });

    this.#recommendationsObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  #clearRecommendationRefreshTimeouts() {
    this.#recommendationRefreshTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.#recommendationRefreshTimeouts = [];
  }

  #queueRecommendationsRefresh() {
    this.#clearRecommendationRefreshTimeouts();

    const timeoutId = window.setTimeout(() => {
      this.#refreshRecommendations();
      this.#recommendationRefreshTimeouts = this.#recommendationRefreshTimeouts.filter((id) => id !== timeoutId);
    }, 120);

    this.#recommendationRefreshTimeouts.push(timeoutId);
  }

  #refreshRecommendations() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#loadCartRecommendations();
      });
    });
  }

  #getCurrentCartProductIds() {
    const container = this.querySelector('[data-cart-recommendations]');
    const idsAttr = container?.getAttribute('data-recommendation-product-ids');
    if (!idsAttr) return [];
    return idsAttr.split(',').map((v) => v.trim()).filter(Boolean);
  }

  async #loadCartRecommendations() {
    const container = this.querySelector('[data-cart-recommendations]');
    const track = container?.querySelector('[data-cart-recommendations-track]');

    if (!(container instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    // Suppress the recommendations observer while we mutate the DOM below: the track write plus
    // Swiper init/refresh add and remove the fraction spans, which would otherwise re-trigger the
    // observer (notably the span-removal branch) and loop. Cleared on the next frame, once the
    // observer has drained our mutations from the microtask queue.
    this.#isUpdatingRecommendations = true;

    this.#renderFallbackRecommendations(container, track);
    this.#updateRecommendationVisibility(container);
    this.#initRecommendationSliders();

    window.requestAnimationFrame(() => {
      this.#isUpdatingRecommendations = false;
    });
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLElement} track
   */
  #renderFallbackRecommendations(container, track) {
    const fallbackTemplate = container.querySelector('[data-cart-recommendations-fallback]');
    if (!(fallbackTemplate instanceof HTMLTemplateElement)) return;

    const cartProductIds = this.#getCurrentCartProductIds();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = fallbackTemplate.innerHTML.trim();

    if (cartProductIds.length > 0) {
      tempDiv.querySelectorAll('[data-product-id]').forEach((card) => {
        const cardEl = /** @type {HTMLElement} */ (card);
        if (cartProductIds.includes(cardEl.dataset.productId || '')) {
          cardEl.closest('.swiper-slide')?.remove();
        }
      });
    }

    // Skip the DOM write when the track already shows exactly these products. Swiper mutates the
    // slides (adds classes and inline styles) after init, so comparing raw innerHTML always
    // mismatches and would rewrite the track on every refresh — destroying the live slider and
    // making the whole block flicker, especially on reopen. Compare the product-id set instead,
    // which is stable across Swiper's mutations.
    const desiredIds = Array.from(tempDiv.querySelectorAll('[data-product-id]'), (el) => /** @type {HTMLElement} */ (el).dataset.productId || '');
    const currentIds = Array.from(track.querySelectorAll('[data-product-id]'), (el) => /** @type {HTMLElement} */ (el).dataset.productId || '');
    if (desiredIds.length === currentIds.length && desiredIds.every((id, index) => id === currentIds[index])) {
      return;
    }

    const filteredHtml = tempDiv.innerHTML.trim();
    if (!filteredHtml) return;

    this.#setRecommendationTrackHtml(track, filteredHtml);
  }

  /**
   * Hides the entire recommendation block when track has no slides.
   * @param {HTMLElement} container
   */
  #updateRecommendationVisibility(container) {
    const track = container.querySelector('[data-cart-recommendations-track]');
    const hasSlides = track && track.children.length > 0;
    container.style.display = hasSlides ? '' : 'none';
  }

  /**
   * @param {HTMLElement} track
   * @param {string} html
   */
  #setRecommendationTrackHtml(track, html) {
    if (track.innerHTML.trim() === html) return;

    this.#isUpdatingRecommendations = true;
    track.innerHTML = html;

    window.requestAnimationFrame(() => {
      this.#isUpdatingRecommendations = false;
    });
  }

  #initRecommendationSliders() {
    if (typeof window.Swiper !== 'function') {
      // Swiper ships as a deferred script; the drawer can be opened (commonly with an empty cart)
      // before it executes. Reveal the raw cards so the upsell isn't invisible, but keep retrying
      // (bounded) so the slider gets a real init — with its fraction pagination — once the library
      // loads. Without the retry the slider stays un-initialized and the pagination count (the
      // `swiper-pagination-current`/`-total` spans) is never rendered.
      this.querySelectorAll('[data-cart-recommendations-slider]').forEach((slider) => {
        slider.classList.add('swiper-initialized');
      });
      if (this.#swiperLoadRetries < 20) {
        this.#swiperLoadRetries += 1;
        this.#queueRecommendationsRefresh();
      }
      return;
    }

    this.#swiperLoadRetries = 0;

    this.querySelectorAll('[data-cart-recommendations]').forEach((container) => {
      const slider = container.querySelector('[data-cart-recommendations-slider]');
      const nextButton = container.querySelector('[data-cart-recommendations-direction="next"]');
      const prevButton = container.querySelector('[data-cart-recommendations-direction="prev"]');

      if (!(slider instanceof HTMLElement) || !(nextButton instanceof HTMLElement) || !(prevButton instanceof HTMLElement)) return;

      const existingSwiper = this.#recommendationSwipers.get(slider);
      if (existingSwiper) {
        const sliderConnected = existingSwiper.el?.isConnected ?? false;
        const livePagination = slider.querySelector('.swiper-pagination');

        const fractionIntact =
          sliderConnected &&
          !!livePagination &&
          livePagination === existingSwiper.pagination?.el &&
          !!livePagination.querySelector('.swiper-pagination-current');

        if (fractionIntact) {
          // Slides changed but the swiper DOM is intact; just re-measure and refill the numbers.
          existingSwiper.update();
          this.#refreshRecommendationPagination(existingSwiper);
          return;
        }

        existingSwiper.destroy(true, sliderConnected);
        this.#recommendationSwipers.delete(slider);
      
        slider.classList.remove('swiper-initialized');
      }
    
      if (slider.offsetWidth === 0) {
        if (this.#sliderLayoutRetries < 10) {
          this.#sliderLayoutRetries += 1;
          this.#queueRecommendationsRefresh();
        }
        return;
      }

      this.#sliderLayoutRetries = 0;

      const paginationEl = slider.querySelector('.swiper-pagination');
      const scrollbarEl = slider.querySelector('.swiper-scrollbar');

      const swiper = new window.Swiper(slider, {
        slidesPerView: 1.15,
        spaceBetween: 16,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        mousewheel: {
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: true,
        },
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        pagination: paginationEl
          ? {
              el: paginationEl,
              type: 'fraction',
              formatFractionCurrent: (/** @type {number} */ n) => (n < 10 ? '0' + n : n),
              formatFractionTotal: (/** @type {number} */ n) => (n < 10 ? '0' + n : n),
            }
          : false,
        scrollbar: scrollbarEl ? { el: scrollbarEl } : false,
        breakpoints: {
          750: {
            slidesPerView: 1.47447,
            spaceBetween: 15,
          },
        },
      });

      this.#recommendationSwipers.set(slider, swiper);
    });
  }

  /**
   * Rebuilds the fraction pagination DOM. `swiper.update()` re-measures slides but does not
   * regenerate the `swiper-pagination-current`/`swiper-pagination-total` spans, so when those
   * get wiped (e.g. a section morph re-renders an empty `.swiper-pagination`) we must render
   * them again, then refill the numbers.
   * @param {any} swiper
   */
  #refreshRecommendationPagination(swiper) {
    const pagination = swiper?.pagination;
    if (!pagination?.el) return;

    pagination.render();
    pagination.update();
  }

  /**
   * Synchronously restores any recommendation pagination/scrollbar that a section morph just
   * emptied, so they're back before the browser paints (no flicker). Swiper injects the fraction
   * spans and the scrollbar drag at init and does NOT recreate them on its own, while a cart morph
   * re-renders both containers empty. The common case — the nodes survived but were stripped — is
   * restored in place; if the live instance is gone (e.g. the slider node was replaced) we defer to
   * the normal debounced (re)init instead.
   */
  #restoreRecommendationControls() {
    let needsFullRefresh = false;

    this.querySelectorAll('[data-cart-recommendations-slider]').forEach((slider) => {
      if (!(slider instanceof HTMLElement)) return;

      const swiper = this.#recommendationSwipers.get(slider);
      if (!swiper || !swiper.el?.isConnected) {
        needsFullRefresh = true;
        return;
      }

      // Fraction pagination: re-render the spans + modifier classes the morph stripped.
      const livePagination = slider.querySelector('.swiper-pagination');
      if (swiper.pagination && livePagination && !livePagination.querySelector('.swiper-pagination-current')) {
        swiper.pagination.el = livePagination;
        livePagination.classList.add('swiper-pagination-fraction', 'swiper-pagination-horizontal');
        swiper.pagination.render();
        swiper.pagination.update();
      }

      // Scrollbar: recreate the drag element Swiper injected, then re-size/position it in place.
      const liveScrollbar = slider.querySelector('.swiper-scrollbar');
      if (swiper.scrollbar && liveScrollbar && !liveScrollbar.querySelector('.swiper-scrollbar-drag')) {
        const dragEl = document.createElement('div');
        dragEl.className = 'swiper-scrollbar-drag';
        liveScrollbar.appendChild(dragEl);
        swiper.scrollbar.el = liveScrollbar;
        swiper.scrollbar.dragEl = dragEl;
        swiper.scrollbar.updateSize?.();
        swiper.scrollbar.setTranslate?.();
      }
    });

    if (needsFullRefresh) this.#queueRecommendationsRefresh();
  }

  /**
   * @param {string | number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    const value = Number(cents) / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(value);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeAttribute(value) {
    return this.#escapeHtml(value).replace(/"/g, '&quot;');
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
